// @ts-strict-ignore
import moment from 'moment';
import { SearchFilterTypes } from '@usealloy/component-library';

import { toSnake } from '~/helpers/api';

import {
  DEPRECATED_FILTER_KEYS,
  DEPRECATED_FILTER_MAPPING,
  INVESTIGATION_AUTO_ASSIGN,
  INVESTIGATION_STATES,
} from '../constants';
import { InvestigationTableSortOrder } from '../types';
import type { CreateInvestigationForm } from './constants';

export const prepareDate = (str: string, round?: 'end' | 'start') => {
  if (round === 'end') {
    return moment(str).endOf('day').toISOString();
  }
  if (round === 'start') {
    return moment(str).startOf('day').toISOString();
  }
  return moment(str).toISOString();
};

export const initEmptyParamsObject = filters => filters.reduce((obj, filter) => {
  if ([
    SearchFilterTypes.singleselect,
    SearchFilterTypes.multiselect,
    SearchFilterTypes.textOnly,
  ].includes(filter.type)) {
    return { ...obj, [filter.reduxKey]: [] };
  }
  if (filter.type === SearchFilterTypes.dateRange) {
    return {
      ...obj,
      [`${filter.reduxKey}From`]: null,
      [`${filter.reduxKey}To`]: null,
    };
  }
  return { ...obj, [filter.reduxKey]: null };
}, {});

export const getQueryParams = ({
  search,
  filters,
  sorting = null,
  initialValues = {},
}) => {
  const params = new URLSearchParams(search);
  const hasQueryParams = params.size > 0;
  const filtersParams = params.get('q') ?? '{}';

  let sortColumn = params.get('sortColumn') ?? null;
  let sortOrder = params.get('sortOrder') ?? null;

  /**
   * If `sorting` is an array, it was provided by the Table component's onTableStateChange.
   * Otherwise, it was omitted as an argument in other usages of getQueryParams.
   *
   * If array, use new sort config or clear if empty (below).
   * Otherwise, fall back to URL params (above).
   */
  if (Array.isArray(sorting)) {
    if (sorting.length) {
      sortColumn = sorting?.[0]?.id ? toSnake(sorting[0].id) : null;
      sortOrder = sorting[0]?.desc === true
        ? InvestigationTableSortOrder.DESC
        : InvestigationTableSortOrder.ASC;
    } else {
      sortColumn = null;
      sortOrder = null;
    }
  }

  const filterReduxKeys = Object.values(filters)
    .flatMap(({ reduxKey }) => (reduxKey ? [reduxKey] : []));
  const cleanInitialValues = Object.entries(initialValues).reduce((acc, [key, val]) => {
    if (filterReduxKeys.some(reduxKey => key.startsWith(reduxKey))) {
      acc[key] = val;
    }

    return acc;
  }, {});

  const init = {
    ...initEmptyParamsObject(filters),
    // both page and perPage should be renamed for the InvestigationTable:tableState
    page: Number(params.get('pageNum') ?? 1),
    perPage: Number(params.get('numRowsPerPage') ?? 20),
    column: sortColumn,
    order: sortOrder,
    isSandbox: params.get('get_field_is_sandbox') === 'true',
    ...(!hasQueryParams ? cleanInitialValues : {}),
    ...JSON.parse(filtersParams),
  };

  try {
    if (init.closedAtFrom || init.closedAtTo) {
      init.closedAtFrom = init.closedAtFrom || prepareDate(init.closedAtTo, 'start');
      init.closedAtTo = init.closedAtTo || prepareDate(init.closedAtFrom, 'end');
    }

    if (init.createdAtFrom || init.createdAtTo) {
      init.createdAtFrom = init.createdAtFrom || prepareDate(init.createdAtTo, 'start');
      init.createdAtTo = init.createdAtTo || prepareDate(init.createdAtFrom, 'end');
    }

    if (init.updatedAtFrom || init.updatedAtTo) {
      init.updatedAtFrom = init.updatedAtFrom || prepareDate(init.updatedAtTo, 'start');
      init.updatedAtTo = init.updatedAtTo || prepareDate(init.updatedAtFrom, 'end');
    }
  } catch (err) {
    return init;
  }
  return init;
};

export const prepareUnixTime = (str: string) => new Date(prepareDate(str)).getTime();

export const getParamsFromValue = (value, filters, resetPage = false) => {
  const params = {} as { [key: string]: string | boolean };
  const { page, perPage } = getQueryParams({
    filters: Object.values(filters),
    search: location.search,
  });
  Object.keys(value)?.forEach(label => {
    const { reduxKey, getValue } = filters[label];
    const paramValue = getValue(label, value);

    switch (reduxKey) {
      case 'caseReviewAt':
      case 'closedAtConfirmedRisk':
      case 'closedAtDismissed':
      case 'updatedAt':
      case 'createdAt': {
        params[`${reduxKey}From`] = prepareDate(paramValue.from);
        params[`${reduxKey}To`] = prepareDate(paramValue.to);
        break;
      }
      default: params[reduxKey] = paramValue;
    }
  });
  return {
    ...params,
    perPage,
    page: resetPage ? 1 : page,
  };
};

export const serializeObject = (obj: { [key: string]: any }) => {
  const outsideParams = {
    page: 1,
    perPage: 20,
    column: null,
    order: null,
  };
  const query = Object.keys(obj)
    .map(key => {
      const cursor = obj[key];
      if (!cursor) return null;
      if ([
        'page',
        'perPage',
        'column',
        'order',
      ].includes(key)) {
        outsideParams[key] = cursor;
        return null;
      }

      const isArray = Array.isArray(cursor);
      switch (typeof cursor) {
        case 'string':
          return `"${key}":"${cursor}"`;
        case 'object':
          if (isArray && !cursor.length) return null;
          if (isArray) return `"${key}":["${cursor.join('","')}"]`;
          return `"${key}":${cursor}`;
        default:
          return `"${key}":${cursor}`; // number | boolean
      }
    }).filter(value => value);
  const paging = [
    `pageNum=${outsideParams.page}`,
    `numRowsPerPage=${outsideParams.perPage}`,
    ...(outsideParams.column ? [`sortColumn=${outsideParams.column}`] : []),
    ...(outsideParams.order ? [`sortOrder=${outsideParams.order}`] : []),
  ].join('&');
  if (query.length === 0) return paging;
  return encodeURI(`q={${query.join(',')}}&${paging}`);
};

export const loadSavedSearchQueryJson = ({ filters, searchQuery }) => (
  Object.entries(searchQuery)
    .filter(([key]) => key !== 'perPage')
    .reduce((acc, [key, value]) => {
      if (key.includes('created') || key.includes('updated')) {
        const label = key.includes('created') ? 'createdAt' : 'updatedAt';
        const filter = Object.values(filters).find(({ reduxKey }) => reduxKey === label) as {
          label: string;
        } | undefined;
        if (filter) {
          if (key.includes('From')) {
            return { ...acc, [filter.label]: { ...acc[filter.label], from: value } };
          }
          return ({ ...acc, [filter.label]: { ...acc[filter.label], to: value } });
        }
      }
      const filter = Object.values(filters).find(({ reduxKey }) => reduxKey === key) as {
        label: string;
      } | undefined;
      if (filter) {
        let newValue = null;
        if (Array.isArray(value)) {
          newValue = value.reduce((valObj, valueItem) => ({ ...valObj, [valueItem]: true }), {});
        } else {
          newValue = { [value as string]: true };
        }
        return { ...acc, [filter?.label]: newValue };
      }
      // Handle filters that have been replaced with newer keys
      if (DEPRECATED_FILTER_KEYS.includes(key as (typeof DEPRECATED_FILTER_KEYS)[number])) {
        const label = DEPRECATED_FILTER_MAPPING[key];
        let newValue = null;
        if (Array.isArray(value)) {
          newValue = value.reduce((valObj, valueItem) => ({ ...valObj, [valueItem]: true }), {});
        } else {
          newValue = { [value as string]: true };
        }
        return acc[label]
          ? { ...acc, [label]: { ...acc[label], ...newValue } }
          : { ...acc, [label]: newValue };
      }
      return acc;
    }, {})
);

export const prepareFormValues = <T = unknown>(value: T, key?: string): T => {
  if (value === null || value === undefined || value === '') {
    return undefined as T;
  }

  if (Array.isArray(value)) {
    return value.map(item => prepareFormValues(item, key)) as T;
  }

  if (typeof value === 'object') {
    const cleanedObj = Object.fromEntries(
      Object.entries(value).map(
        ([propKey, propVal]) => [propKey, prepareFormValues(propVal, key ? `${key}.${propKey}` : propKey)],
      ),
    ) as T & object;

    return cleanedObj;
  }

  return value;
};

export const formatTagEvidences = ({ tags, outcomeReasons }) => ([
  ...outcomeReasons.map(({ tag_name }) => ({
    is_outcome_reason: true,
    tag_name,
  })),
  ...tags.map(({ tag_name }) => ({
    is_outcome_reason: false,
    tag_name,
  })),
]);

export const formatPostInvestigationBody = (formValues: CreateInvestigationForm) => {
  const {
    entity_token: entityToken,
    investigation_assignee_token: investigationAssigneeToken,
    investigation_status: investigationStatus,
    outcome_reasons: outcomeReasons,
    tags,
    /**
     * Pull these out of the form state but do not use because they are required for the UI
     * but not actually part of the API request
     */
    is_creating_new_investigation_type: isCreatingNewInvestigationType,
    use_case: useCase,
    ...rest
  } = formValues;
  const entityTokens = [entityToken];

  const sendInvestigationStatus = investigationStatus
    && !Object.values(INVESTIGATION_STATES).includes(investigationStatus);

  return {
    entity_tokens: entityTokens,
    investigation_assignee_token: investigationAssigneeToken === INVESTIGATION_AUTO_ASSIGN
      ? undefined : investigationAssigneeToken,
    ...(sendInvestigationStatus && { investigation_status: investigationStatus }),
    should_auto_assign: investigationAssigneeToken === INVESTIGATION_AUTO_ASSIGN,
    tags: formatTagEvidences({ outcomeReasons, tags }),
    ...rest,
  };
};
